import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { useNavigate, useOutletContext } from "react-router";
import { toast } from "react-toastify";
import {
  ITeacherPassageQuestion,
  getTeacherPassageById,
  getTeacherPassageQuestions,
  updateTeacherPassage,
} from "../../../../api/crmContent.service";
import { getAllgrade } from "../../../../api/grade.service";
import { ISelectElement } from "../../../../model/interface/keyValuePair";
import Loader from "../../../shared/loader/loader";
import LearnosityPassageEditor from "../../passage/learnosityPassageEditor";
import { IUserContentOutletContext } from "./UserContentLayout";
import { useNumericParam } from "../../../../utils/useNumericParam";
import { EditIcon, PreviewIcon } from "../../../../assets/icons/index";
import Pagination from "../../../shared/common/pagination";
import SelectPageSize from "../../../shared/common/pageSizeDropdown";
import Constant from "../../../../utils/constant";
import PreviewModal from "../../questions/previewModal";
import {
  defaultAssociatedQuestionPageSize,
  getPageRange,
  getTotalPages,
  paginate,
  toPlainText,
} from "./teacherPassageHelpers";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import store from "../../../../redux/store";

const defaultGradeOption: ISelectElement = { label: "Select", value: "0" };

function wordCount(str: string): number {
  return (
    str
      .trim()
      // eslint-disable-next-line no-control-regex
      .split(new RegExp(" |\n"))
      .filter((n) => n !== "").length
  );
}

export const TeacherPassageEditor: React.FC = () => {
  const { userId, userInfo } = useOutletContext<IUserContentOutletContext>();
  const teacherPassageId = useNumericParam("teacherPassageId");
  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cmsAuthorUserId: number =
    (store.getState() as any).profile?.userId ?? 0;

  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [gradesLoading, setGradesLoading] = useState<boolean>(true);
  const [lrnLoading, setLrnLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [grades, setGrades] = useState<Array<ISelectElement>>([]);
  const [selectedGrade, setSelectedGrade] =
    useState<ISelectElement>(defaultGradeOption);
  const [rawGradeId, setRawGradeId] = useState<string>("0");
  const [isApproved, setIsApproved] = useState<boolean>(false);

  const [title, setTitle] = useState<string>("");
  const [passageReferenceId, setPassageReferenceId] = useState<string>("");
  const [cmsAuthorNotes, setCmsAuthorNotes] = useState<string>("");
  const [wordCountValue, setWordCountValue] = useState<number>(0);
  // JSON of the widget as the editor last loaded it, so opening the preview
  // only snapshots the widget when there are unsaved edits.
  const loadedWidgetJsonRef = useRef<string | null>(null);
  const [, setIsLrnPaginated] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const authorAppRef = useRef<any>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [widgetPsgDataEN, setWidgetPsgDataEN] = useState<any>(null);

  const [questions, setQuestions] = useState<Array<ITeacherPassageQuestion>>(
    []
  );
  const [questionsLoading, setQuestionsLoading] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(
    defaultAssociatedQuestionPageSize
  );
  const paginationRef = useRef<{ onReset: () => void }>(null);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewItemRef, setPreviewItemRef] = useState<string>("");
  const [questionIdPreview, setQuestionIdPreview] = useState<number>();
  const [questionIndexPreview, setQuestionIndexPreview] = useState<number>(0);
  // Bumped when the preview closes: the preview runs a second Learnosity
  // Author instance that takes over #learnosity-author, so the passage editor
  // is remounted (restoring unsaved edits from widgetPsgDataEN).
  const [editorKey, setEditorKey] = useState<number>(0);

  const bindGrades = () => {
    getAllgrade().then((response) => {
      const options: Array<ISelectElement> = (response.data?.data ?? []).map(
        (grade: { gradeId: number; name: string }) => ({
          value: String(grade.gradeId ?? 0),
          label: grade.name,
        })
      );
      setGrades([defaultGradeOption, ...options]);
      setGradesLoading(false);
    });
  };

  useEffect(() => {
    bindGrades();
  }, []);

  useEffect(() => {
    if (!teacherPassageId) return;
    setDataLoading(true);
    getTeacherPassageById(teacherPassageId, userInfo?.districtId ?? 0)
      .then((response) => {
        const data = response.data;
        setTitle(data.title ?? "");
        setPassageReferenceId(
          data.passageReferenceId || `${teacherPassageId}-p-teacher`
        );
        setCmsAuthorNotes(data.cmsAuthorNotes ?? "");
        setWordCountValue(data.wordCount ?? 0);
        setIsApproved(data.isApproved);
        if (data.gradeId) {
          setRawGradeId(String(data.gradeId));
        }
        // For a new passage (no prior Learnosity content), pre-populate the
        // widget with the title so the heading field isn't blank on first load.
        if (data.title && !data.wordCount && !data.passageReferenceId) {
          setWidgetPsgDataEN({
            type: "sharedpassage",
            content: "",
            heading: data.title,
          });
        }
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          toast.error("Passage not found or not accessible.");
          navigate("..", { replace: true });
        } else {
          toast.error("Failed to load passage.");
        }
      })
      .finally(() => setDataLoading(false));
  }, [teacherPassageId]);

  useEffect(() => {
    if (!teacherPassageId) return;
    setQuestionsLoading(true);
    getTeacherPassageQuestions(teacherPassageId, userInfo?.districtId ?? 0)
      .then((response) => setQuestions(response.data ?? []))
      .catch((err) => {
        setQuestions([]);
        if (err?.response?.status !== 404)
          toast.error("Failed to load associated questions.");
      })
      .finally(() => setQuestionsLoading(false));
  }, [teacherPassageId]);

  useEffect(() => {
    setPageNumber(1);
    paginationRef.current?.onReset();
  }, [pageSize]);

  useEffect(() => {
    if (rawGradeId === "0" || grades.length <= 1) return;
    const match = grades.find((g) => g.value === rawGradeId);
    if (match) setSelectedGrade(match);
  }, [rawGradeId, grades]); // eslint-disable-line

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const authorAppParent = (app: any) => {
    authorAppRef.current = app;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setWidgetPsgEN = (data: any) => {
    setWidgetPsgDataEN(data);
    if (data?.content) {
      setWordCountValue(wordCount(data.content));
    } else if (data?.pages) {
      const combined = (data.pages as string[]).join(" ");
      setWordCountValue(wordCount(combined));
    }
  };

  const getWordCount = (): number => {
    if (!authorAppRef.current) return wordCountValue;
    const widget = authorAppRef.current.getWidget();
    if (widget?.content) return wordCount(widget.content);
    if (widget?.pages) {
      return wordCount((widget.pages as string[]).join(" "));
    }
    return wordCountValue;
  };

  const handleSave = () => {
    if (!teacherPassageId) return;
    const gradeIdNum = Number(selectedGrade.value);
    if (!gradeIdNum) {
      toast.error("Please select a grade.");
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const widget: any = authorAppRef.current?.getWidget?.();
    const passageWidgetJson = widget ? JSON.stringify(widget) : undefined;
    const heading: string = widget?.heading ?? title;
    const passageText: string = widget?.content ?? "";
    setSaving(true);
    updateTeacherPassage({
      teacherPassageId,
      userId,
      cmsAuthorUserId: cmsAuthorUserId || undefined,
      title: heading,
      passage: passageText,
      passageReferenceId,
      gradeId: gradeIdNum,
      wordCount: getWordCount(),
      isApproved,
      isActive: true,
      cmsAuthorNotes,
      passageWidgetJson,
    })
      .then(() => {
        toast.success("Passage saved.");
        navigate("../passages");
      })
      .catch(() => toast.error("Failed to save passage."))
      .finally(() => setSaving(false));
  };

  const questionsForPreview = useMemo(
    () =>
      questions.map((q) => ({
        questionId: q.teacherQuestionId,
        englishQuestionReferenceId: `${q.teacherQuestionId}-teacher`,
      })),
    [questions]
  );
  const totalQuestions = questions.length;
  const totalPages = getTotalPages(totalQuestions, pageSize);
  const visibleQuestions = useMemo(
    () => paginate(questions, pageNumber, pageSize),
    [questions, pageNumber, pageSize]
  );
  const pageRange = getPageRange(totalQuestions, pageNumber, pageSize);

  const getQuestionEditUrl = (teacherQuestionId: number) =>
    Constant.crmUserContentQuestionEdit
      .replace(":userid", String(userId))
      .replace(":teacherQuestionId", String(teacherQuestionId));

  // Runs on Learnosity's widgetedit:widget:ready, once per (re)mount.
  const handleLrnEditorLoaded = () => {
    const widget = authorAppRef.current?.getWidget?.();
    loadedWidgetJsonRef.current = widget ? JSON.stringify(widget) : null;
  };

  const handleOpenPreview = (teacherQuestionId: number, rowIndex: number) => {
    // Snapshot the live widget before the preview takes over
    // #learnosity-author: widgetPsgDataEN only updates on
    // widgetedit:preview:changed, which edits such as the heading don't fire,
    // and the remount on close restores from it.
    const liveWidget = authorAppRef.current?.getWidget?.();
    if (
      liveWidget &&
      JSON.stringify(liveWidget) !== loadedWidgetJsonRef.current
    )
      setWidgetPsgEN(liveWidget);
    setPreviewItemRef(`${teacherQuestionId}-teacher`);
    setQuestionIdPreview(teacherQuestionId);
    setQuestionIndexPreview((pageNumber - 1) * pageSize + rowIndex);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setEditorKey((k) => k + 1);
  };

  if (dataLoading || gradesLoading) return <Loader />;

  return (
    <>
      {previewOpen && (
        <PreviewModal
          show={previewOpen}
          changeValue={(open: boolean) => {
            if (!open) handleClosePreview();
          }}
          itemRef={previewItemRef}
          isTeacherQuestion={true}
          questionIdPreview={questionIdPreview}
          questionIndexPreview={questionIndexPreview}
          setQuestionIndexPreview={setQuestionIndexPreview}
          setQuestionIdPreview={setQuestionIdPreview}
          setPreviewId={setPreviewItemRef}
          setQuestionToPreview={() => undefined}
          teacherQuestionOwnerUserId={userId}
          questions={questionsForPreview}
        />
      )}
      <div className="flex-1 overflow-auto">
        <div className="h-auto min-h-half w-full px-8 py-6">
          <div className="flex flex-col gap-4 w-full">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex flex-col gap-1 min-w-[200px]">
                <label className="text-sm font-medium text-gray-700">
                  Grade
                </label>
                <Select
                  options={grades}
                  value={selectedGrade}
                  onChange={(option) =>
                    setSelectedGrade(option ?? defaultGradeOption)
                  }
                  className="w-48"
                />
              </div>
              <div className="flex items-center gap-2 mt-4">
                <input
                  id="isApproved"
                  type="checkbox"
                  checked={isApproved}
                  onChange={(e) => setIsApproved(e.target.checked)}
                  className="h-4 w-4"
                />
                <label
                  htmlFor="isApproved"
                  className="text-sm font-medium text-gray-700"
                >
                  Approved
                </label>
              </div>
            </div>

            <div className="flex flex-row items-stretch space-x-8 w-full">
              <div className="flex-1 min-w-0">
                <div>Word Count: {wordCountValue}</div>
                {lrnLoading && <Loader />}
                <LearnosityPassageEditor
                  key={editorKey}
                  authorAppParent={authorAppParent}
                  widgetPsgDataEN={widgetPsgDataEN}
                  setWidgetPsgEN={setWidgetPsgEN}
                  englishPsgReferenceId={passageReferenceId}
                  setIsLrnEditorLoaded={handleLrnEditorLoaded}
                  setIsLrnPaginated={setIsLrnPaginated}
                  setLoading={setLrnLoading}
                />
              </div>
              <div className="flex-shrink-0 w-96 flex flex-col">
                <h4 className="text-gray-700">CMS Author Notes</h4>
                <textarea
                  name="cmsAuthorNotes"
                  value={cmsAuthorNotes}
                  onChange={(e) => setCmsAuthorNotes(e.target.value)}
                  className="flex-1 resize border border-gray-300 rounded-md w-full dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white"
                />
              </div>
            </div>

            <div>
              <button
                type="button"
                disabled={saving}
                onClick={handleSave}
                className="inline-flex items-center px-4 py-2 bg-primary-color text-white text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>

            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Associated Questions ({totalQuestions})
              </h4>
              {questionsLoading ? (
                <Loader />
              ) : totalQuestions === 0 ? (
                <p className="text-sm text-gray-500">
                  No questions are associated with this passage.
                </p>
              ) : (
                <>
                  <div className="shadow border-b border-gray-200 sm:rounded-lg">
                    <table className="w-full table-fixed divide-y divide-gray-200">
                      <thead className="bg-primary-color">
                        <tr>
                          <th
                            scope="col"
                            className="px-3 py-2 w-24 font-light text-sm text-left text-white dark:text-white tracking-wider whitespace-nowrap rounded-tl-lg"
                          >
                            Id
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider whitespace-nowrap"
                          >
                            Question Text
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-2 w-20 font-light text-sm text-center text-white dark:text-white tracking-wider rounded-tr-lg"
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleQuestions.map((question, index) => {
                          const plainText = toPlainText(question.questionText);
                          return (
                            <tr
                              key={question.teacherQuestionId}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }
                            >
                              <td className="px-3 py-2 text-sm font-normal">
                                <a
                                  href={getQuestionEditUrl(
                                    question.teacherQuestionId
                                  )}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(
                                      getQuestionEditUrl(
                                        question.teacherQuestionId
                                      )
                                    );
                                  }}
                                  className="text-primary-color hover:underline"
                                >
                                  {question.teacherQuestionId}
                                </a>
                              </td>
                              <td className="px-3 py-2 text-sm font-normal text-gray-700">
                                {plainText ? (
                                  <span
                                    className="block truncate"
                                    title={plainText}
                                  >
                                    {plainText}
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                <div className="flex justify-center gap-2">
                                  <EditIcon
                                    title="Edit question"
                                    onClick={() =>
                                      navigate(
                                        getQuestionEditUrl(
                                          question.teacherQuestionId
                                        )
                                      )
                                    }
                                    className="h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                  />
                                  <PreviewIcon
                                    title="Preview question"
                                    onClick={() =>
                                      handleOpenPreview(
                                        question.teacherQuestionId,
                                        index
                                      )
                                    }
                                    className="h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center mt-2">
                    {totalPages > 1 && (
                      <Pagination
                        pages={totalPages}
                        setPageNumber={setPageNumber}
                        ref={paginationRef}
                      />
                    )}
                    <span className="ml-auto">
                      <span className="text-sm pr-4 ml-auto text-left-700">
                        Showing {pageRange.from} to {pageRange.to} of{" "}
                        {totalQuestions} results
                      </span>
                      <SelectPageSize
                        setPageSize={setPageSize}
                        type={"second"}
                        defaultPage={defaultAssociatedQuestionPageSize}
                      />
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
